import { useEffect, useRef } from 'react';
import { canOpenPaywall, shouldShowFreeBanner } from '@/access/access';
import { useAccess } from '@/access/useAccess';
import { track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';
import { paidSubscriptionOf } from '@/auth/mapUser';
import { usePaywall } from '@/billing/PaywallContext';
import { usePaywallPrice } from '@/billing/usePaywallPrice';
import { useT } from '@/i18n/LocaleProvider';
import { useVideoDetail } from './VideoDetailContext';
import styles from './VideoDrawer.module.css';

/**
 * Первая строка тела панели разбора, когда открытие этого видео списало последний
 * бесплатный просмотр дня: что открывает платный тариф (строка реестра) и Upgrade
 * с ценой плана, который окно выберет само, — окно открывается без плана и подсвечивает
 * ту же цену. Стена не меняется — следующий клик по карточке, как и раньше, открывает
 * окно `detail_quota`. Без цены строка не рисуется.
 */
export function LastFreeViewNote({ videoId }: { videoId: number }) {
  const t = useT();
  const access = useAccess();
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const { lastFreeViewId } = useVideoDetail();
  const price = usePaywallPrice();

  const visible =
    lastFreeViewId === videoId &&
    price !== null &&
    shouldShowFreeBanner(access, user) &&
    // Живая платная строка Paddle (past_due · paused): второй чекаут ей не предлагаем — как Upgrade в шапке.
    !paidSubscriptionOf(user) &&
    canOpenPaywall(access);

  // Показ — один на переход к нулю: тот же banner_view, что у баннера бесплатного плана.
  const trackedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!visible) {
      trackedFor.current = null;
      return;
    }
    if (trackedFor.current === videoId) return;
    trackedFor.current = videoId;
    track('banner_view', { metadata: { banner: 'quota_last_view' } });
  }, [visible, videoId]);

  if (!visible) return null;

  return (
    <div className={styles.lastView} data-last-free-view>
      <p className={styles.lastViewText}>{t('paywall.headline.detailViews')}</p>
      <button
        type="button"
        className={styles.lastViewUpgrade}
        onClick={() =>
          openPaywall({ trigger: 'intent', source: 'quota_last_view', reason: 'detail_quota' })
        }
      >
        {t('nav.upgrade')} <span className={styles.lastViewPrice}>{price}</span>
      </button>
    </div>
  );
}
